import type * as Phaser from 'phaser';
import {
  DEFAULT_CAP_FLOCK_AMOUNT,
  DEFAULT_WATERLINE_HINT_ALPHA,
  MeshPaintSink,
  dashedPolyline,
  elevatedBodiesInPaintOrder,
  evaluateSun,
  fillClosedRing,
  fillExtrudedRing,
  islandAppearance,
  paintIslandSkirt,
  resolveColor,
  resolveElevationStack,
  strokeClosedRing,
  strokeExtrudedRing,
  strokeHintRuns,
  tessellateRing,
  waterlineHintRunsById,
  MAX_WATER_DEPTH,
  type ElevatedPaintBody,
  type MapDocument,
  type PaintSink,
  type Ring,
} from '../sim/map/index.ts';
import { sunlitBed, waterAppearance } from '../sim/environment/water.ts';
import { bedSinkFactor, mixColor } from './colorMix.ts';
import type { ElevatedBodyGraphicsPool, TerrainPaintMode } from './elevatedLayers.ts';
import { StaticMesh } from './terrain/staticMesh.ts';
import { earcutTriangulate } from './terrain/triangulate.ts';

export { fillExtrudedRing, paintIslandSkirt, strokeExtrudedRing } from '../sim/map/index.ts';

/** Hovered / “armed” start area during placement (solid rim). */
const START_AREA_HOT_ALPHA = 0.55;
/** Placement idle — a bit softer than hot, dashed rim until the pointer enters. */
const START_AREA_DIM_ALPHA = 0.28;

// Screen-pixel measurements; callers scale them into world units for the current zoom.
export const RING_STROKE_PX = 2;
export const RING_SELECTED_STROKE_PX = 3;
export const REEF_SOFT_EDGE_PX = 8;
const START_AREA_HOT_STROKE_PX = 3;
const START_AREA_DIM_STROKE_PX = 2.5;
const START_AREA_DIM_DASH_PX = 8;
const START_AREA_DIM_GAP_PX = 6;

/**
 * A terrain layer is a Graphics (command buffer replayed every frame) or a `StaticMesh`
 * (tessellated once, drawn from a static GPU buffer). Painters see only a `PaintSink`.
 * See docs/PERFORMANCE.md.
 */
export type TerrainLayer = Phaser.GameObjects.Graphics | StaticMesh;

export type TerrainGraphics = {
  /** Shallow-bed fills. */
  water: TerrainLayer;
  /** Shallow rims and reef soft edges (zoom-scaled). Falls back to `water` when absent. */
  waterChrome?: TerrainLayer;
  skirt: TerrainLayer;
  land: Phaser.GameObjects.Graphics;
  rock: Phaser.GameObjects.Graphics;
  /** Top-most land chrome; walls must not hide this stroke. */
  waterlineHint: TerrainLayer;
};

/**
 * Everything a layer's painter reads, in order. Entries are compared with `Object.is`, so a
 * tessellated outline array can stand in for its geometry: `tessellateRing` hands back the
 * same array until the ring changes.
 */
export type LayerKey = readonly unknown[];

/** Key each layer was last painted for. Lives with the layer, so callers stay stateless. */
const layerKeys = new WeakMap<TerrainLayer, LayerKey>();

function sameKey(a: LayerKey | undefined, b: LayerKey): boolean {
  if (!a || a.length !== b.length) {
    return false;
  }

  return a.every((value, index) => Object.is(value, b[index]));
}

/**
 * Repaint a layer from scratch. A mesh layer is rebuilt into one upload; a Graphics layer
 * is cleared and recorded. `paint` receives the sink to draw into.
 *
 * `key` is what the paint depends on. When it matches the last one the layer keeps its mesh
 * and the tessellation is skipped — the shared bed / skirt / hint layers are one mesh each
 * for the whole map, so an island drag used to re-upload every shallow zone on the map
 * (docs/PERFORMANCE.md). Pass `null` to repaint unconditionally.
 */
export function paintLayer(
  layer: TerrainLayer,
  key: LayerKey | null,
  paint: (sink: PaintSink) => void,
): void {
  if (key && sameKey(layerKeys.get(layer), key)) {
    return;
  }

  if (key) {
    layerKeys.set(layer, key);
  } else {
    layerKeys.delete(layer);
  }

  if (layer instanceof StaticMesh) {
    const sink = new MeshPaintSink(earcutTriangulate);
    paint(sink);
    layer.setGeometry(sink.finish());
    return;
  }

  layer.clear();
  paint(layer);
}

/** Wipe a layer without repainting. */
export function clearLayer(layer: TerrainLayer): void {
  layerKeys.delete(layer);

  if (layer instanceof StaticMesh) {
    layer.setGeometry([]);
    return;
  }

  layer.clear();
}

export function paintRing(
  sink: PaintSink,
  ring: Ring,
  fill: number,
  stroke: number,
  strokeWidth: number,
  softEdgeWidth = 0,
  alpha = 1,
): void {
  fillClosedRing(sink, ring, fill, alpha);
  strokeClosedRing(sink, ring, stroke, strokeWidth, alpha, softEdgeWidth);
}

/**
 * Hidden-shore dashes on a top layer. Do not clip against other columns — a
 * terrace wall is the same pixels as the shore it sits on, and clipping there
 * is what hid the stroke on the elevation face. Nested terraces sit on land
 * and get no dash.
 */
export function paintWaterlineHints(
  layer: TerrainLayer,
  map: MapDocument,
  strokeScale: number,
  hintAlpha: number,
  bodyVisible?: (id: string) => boolean,
): void {
  if (hintAlpha <= 0) {
    clearLayer(layer);
    return;
  }

  const byId = waterlineHintRunsById(map);
  const bodies = elevatedBodiesInPaintOrder(map);
  const key: unknown[] = [strokeScale, hintAlpha];

  for (const entry of bodies) {
    const shown = !bodyVisible || bodyVisible(entry.body.id);
    key.push(byId.get(entry.body.id), shown, resolveColor(entry.body.color).alpha);
  }

  paintLayer(layer, key, (sink) => {
    for (const entry of bodies) {
      if (bodyVisible && !bodyVisible(entry.body.id)) {
        continue;
      }

      const runs = byId.get(entry.body.id);

      if (!runs || runs.length === 0) {
        continue;
      }

      const swatch = resolveColor(entry.body.color);
      strokeHintRuns(sink, runs, strokeScale, swatch.alpha, hintAlpha);
    }
  });
}

export type TerrainRingKind = 'island' | 'rock' | 'water';

export type TerrainDrawOptions = {
  strokeScale: number;
  waterlineHintAlpha?: number;
  columns?: ElevatedBodyGraphicsPool;
  /** When false, islands/rocks stay flat (editor authoring). Default true. */
  extrudeColumns?: boolean;
  /** Organic paint on extruded caps. Default true. */
  capFlock?: boolean;
  /** 0–1 flock density. Default `DEFAULT_CAP_FLOCK_AMOUNT` (shipped count). */
  capFlockAmount?: number;
  /** When false, shallow beds use authored colors (no sunlit bed). Default true. */
  lightBeds?: boolean;
  /** Default `'all'`. Zoom restrokes use `'chrome'` so column fills stay. */
  paint?: TerrainPaintMode;
  /** Skip hint dashes for culled columns. Omit to paint every cached run. */
  bodyVisible?: (id: string) => boolean;
  isSelected?: (kind: TerrainRingKind, id: string) => boolean;
  selectedStroke?: number;
  selectedStrokePx?: number;
};

function ringStroke(
  options: TerrainDrawOptions,
  kind: TerrainRingKind,
  id: string,
  strokeScale: number,
  fallback: number,
): { stroke: number; width: number } {
  const selected = options.isSelected?.(kind, id) === true;
  const widthPx = selected
    ? (options.selectedStrokePx ?? RING_SELECTED_STROKE_PX)
    : RING_STROKE_PX;

  return {
    stroke: selected ? (options.selectedStroke ?? fallback) : fallback,
    width: widthPx * strokeScale,
  };
}

/**
 * `strokeScale` is world units per screen pixel (1 / camera zoom). Terrain outlines would
 * otherwise disappear when the player zooms out and turn into slabs when they zoom in.
 *
 * `paint` picks the layers that are rebuilt: `fills` touches beds, skirts, and column
 * fills (map / sun / look changes); `chrome` touches rims and hints (zoom settle). Layers
 * outside the pass keep their command buffer or uploaded mesh.
 */
export function drawTerrain(
  graphics: TerrainGraphics,
  map: MapDocument,
  options: TerrainDrawOptions,
): void {
  const strokeScale = options.strokeScale;
  const waterlineHintAlpha = options.waterlineHintAlpha ?? DEFAULT_WATERLINE_HINT_ALPHA;
  const columns = options.columns;
  const extrudeColumns = options.extrudeColumns !== false;
  const capFlock = options.capFlock !== false;
  const capFlockAmount = options.capFlockAmount ?? DEFAULT_CAP_FLOCK_AMOUNT;
  const lightBeds = options.lightBeds !== false;
  const paint = options.paint ?? 'all';
  const paintFills = paint !== 'chrome';
  const paintChrome = paint !== 'fills';
  const sun = evaluateSun(map.environment);
  const water = waterAppearance(sun.skyLight);
  const stack = resolveElevationStack(map);
  const zones = [...map.shallowWater].sort((a, b) => b.depth - a.depth);
  const zoneLook = (zone: (typeof zones)[number]) => {
    const swatch = resolveColor(zone.color);
    const sink = bedSinkFactor(zone.depth);
    const fill = lightBeds
      ? sunlitBed(mixColor(swatch.fill, water.seafloor, sink), sun.skyLight, water.seafloor)
      : swatch.fill;
    const authoredStroke = lightBeds
      ? sunlitBed(mixColor(swatch.stroke, water.seafloor, sink), sun.skyLight, water.seafloor)
      : swatch.stroke;
    const rim = ringStroke(options, 'water', zone.id, strokeScale, authoredStroke);
    const softEdge = REEF_SOFT_EDGE_PX * (zone.depth / MAX_WATER_DEPTH) * strokeScale;

    return { swatch, fill, rim, softEdge };
  };
  const waterChrome = graphics.waterChrome ?? null;
  // Shared-mesh layers below are one upload for the whole map, so they carry a key: the
  // tessellated outline (identity-stable) plus the numbers the painter reads. An island
  // edit leaves every bed and rim alone.
  const bedFillKey: unknown[] = [];
  const bedRimKey: unknown[] = [];

  for (const zone of zones) {
    const look = zoneLook(zone);
    bedFillKey.push(tessellateRing(zone), look.fill, look.swatch.alpha);
    bedRimKey.push(
      tessellateRing(zone),
      look.rim.stroke,
      look.rim.width,
      look.swatch.alpha,
      look.softEdge,
    );
  }

  if (waterChrome) {
    if (paintFills) {
      paintLayer(graphics.water, bedFillKey, (sink) => {
        for (const zone of zones) {
          const look = zoneLook(zone);
          fillClosedRing(sink, zone, look.fill, look.swatch.alpha);
        }
      });
    }

    if (paintChrome) {
      paintLayer(waterChrome, bedRimKey, (sink) => {
        for (const zone of zones) {
          const look = zoneLook(zone);
          strokeClosedRing(
            sink,
            zone,
            look.rim.stroke,
            look.rim.width,
            look.swatch.alpha,
            look.softEdge,
          );
        }
      });
    }
  } else {
    // One shared layer: any pass repaints fills and rims together.
    const sharedKey =
      paint === 'fills' ? bedFillKey : [...bedFillKey, ...bedRimKey];

    paintLayer(graphics.water, sharedKey, (sink) => {
      for (const zone of zones) {
        const look = zoneLook(zone);

        if (paint === 'fills') {
          fillClosedRing(sink, zone, look.fill, look.swatch.alpha);
        } else {
          paintRing(
            sink,
            zone,
            look.fill,
            look.rim.stroke,
            look.rim.width,
            look.softEdge,
            look.swatch.alpha,
          );
        }
      }
    });
  }

  if (paintFills) {
    graphics.land.clear();
    graphics.rock.clear();

    const skirtKey: unknown[] = [];

    for (const island of map.islands) {
      const look = islandAppearance(island);
      skirtKey.push(
        tessellateRing(island),
        look.skirtM,
        look.skirt,
        resolveColor(island.color).alpha,
      );
    }

    paintLayer(graphics.skirt, skirtKey, (sink) => {
      for (const island of map.islands) {
        const look = islandAppearance(island);
        const swatch = resolveColor(island.color);
        paintIslandSkirt(sink, island, look.skirtM, look.skirt, swatch.alpha);
      }
    });
  }

  const fillBody = (sink: PaintSink, entry: ElevatedPaintBody): void => {
    const resolved = stack.get(entry.body.id);
    const elevation = resolved?.worldElevation ?? entry.body.elevation;
    const baseElevation = resolved?.baseElevation ?? 0;
    const swatch = resolveColor(entry.body.color);

    if (!extrudeColumns) {
      fillClosedRing(sink, entry.body, swatch.fill, swatch.alpha);
      return;
    }

    fillExtrudedRing(sink, {
      ring: entry.body,
      elevation,
      sun,
      fill: swatch.fill,
      alpha: swatch.alpha,
      wallSwatch: entry.kind === 'island' ? islandAppearance(entry.body).cliff : undefined,
      baseElevation,
      id: entry.body.id,
      kind: entry.kind,
      capFlock,
      capFlockAmount,
    });
  };

  const strokeBody = (sink: PaintSink, entry: ElevatedPaintBody): void => {
    const resolved = stack.get(entry.body.id);
    const elevation = resolved?.worldElevation ?? entry.body.elevation;
    const swatch = resolveColor(entry.body.color);
    const rim = ringStroke(options, entry.kind, entry.body.id, strokeScale, swatch.stroke);

    if (!extrudeColumns) {
      strokeClosedRing(sink, entry.body, rim.stroke, rim.width, swatch.alpha);
      return;
    }

    strokeExtrudedRing(sink, entry.body, elevation, rim.stroke, rim.width, swatch.alpha);
  };

  // Change keys so the pool can keep a body's geometry when only another body moved.
  const sunKey = `${sun.direction.x},${sun.direction.y},${sun.direction.z},${sun.irradiance},${sun.skyLight},${sun.strength}`;
  const lookKey = `${extrudeColumns}|${capFlock}|${capFlockAmount}`;
  const fillKey = (entry: ElevatedPaintBody): string => {
    const resolved = stack.get(entry.body.id);
    const swatch = resolveColor(entry.body.color);
    const cliff = entry.kind === 'island' ? islandAppearance(entry.body).cliff : 0;

    return `${sunKey}|${lookKey}|${resolved?.worldElevation ?? entry.body.elevation}|${resolved?.baseElevation ?? 0}|${swatch.fill}|${swatch.alpha}|${cliff}|${entry.body.id}`;
  };
  const strokeKey = (entry: ElevatedPaintBody): string => {
    const resolved = stack.get(entry.body.id);
    const swatch = resolveColor(entry.body.color);
    const rim = ringStroke(options, entry.kind, entry.body.id, strokeScale, swatch.stroke);

    return `${extrudeColumns}|${resolved?.worldElevation ?? entry.body.elevation}|${rim.stroke}|${rim.width}|${swatch.alpha}`;
  };

  if (columns) {
    columns.sync(map, { fill: fillBody, stroke: strokeBody, fillKey, strokeKey }, paint);
  } else if (paintFills) {
    for (const entry of elevatedBodiesInPaintOrder(map)) {
      fillBody(graphics.land, entry);

      if (paintChrome) {
        strokeBody(graphics.land, entry);
      }
    }
  } else if (paintChrome) {
    for (const entry of elevatedBodiesInPaintOrder(map)) {
      strokeBody(graphics.land, entry);
    }
  }

  if (paintChrome) {
    paintWaterlineHints(
      graphics.waterlineHint,
      map,
      strokeScale,
      waterlineHintAlpha,
      options.bodyVisible,
    );
  }
}

/**
 * Play placement look. `hot` is pointer-over (or after plant while aiming keeps
 * the solid fill). Dim is softer fill + dashed rim until hover.
 */
export function drawStartArea(
  gfx: Phaser.GameObjects.Graphics,
  map: MapDocument,
  hot: boolean,
  strokeScale: number,
): void {
  gfx.clear();

  if (!map.startArea) {
    return;
  }

  const swatch = resolveColor(map.startArea.color);
  const fillMul = hot ? START_AREA_HOT_ALPHA : START_AREA_DIM_ALPHA;
  const strokeMul = hot ? 0.9 : 0.55;
  const fillAlpha = swatch.alpha * fillMul;
  const strokeAlpha = swatch.alpha * strokeMul;
  const strokePx =
    (hot ? START_AREA_HOT_STROKE_PX : START_AREA_DIM_STROKE_PX) * strokeScale;
  // Graphics types its point arrays as Vector2; the painter contract is plain `{ x, y }`.
  const sink: PaintSink = gfx;

  sink.fillStyle(swatch.fill, fillAlpha);

  const points = tessellateRing(map.startArea);

  if (points.length < 3) {
    return;
  }

  sink.fillPoints(points, true, true);

  if (hot) {
    sink.lineStyle(strokePx, swatch.stroke, strokeAlpha);
    sink.strokePoints(points, true, true);
    return;
  }

  sink.lineStyle(strokePx, swatch.stroke, strokeAlpha);
  const dash = START_AREA_DIM_DASH_PX * strokeScale;
  const gap = START_AREA_DIM_GAP_PX * strokeScale;

  for (const segment of dashedPolyline(points, true, dash, gap)) {
    gfx.lineBetween(segment.a.x, segment.a.y, segment.b.x, segment.b.y);
  }
}
